import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Upload, X, Cookie, Croissant, Flame, Shirt, Palette, Gem, Lamp, Sparkles, Leaf, Image as ImageIcon, Coffee, Nut, Gift, Package, Heart, Star, ShoppingBag, Home, Music, BookOpen } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import axiosInstance from '../../lib/axios';

// Available icons for categories
const ICON_OPTIONS = [
  { name: 'cookie', icon: Cookie, label: 'Cookie/Sweets' },
  { name: 'croissant', icon: Croissant, label: 'Snacks' },
  { name: 'flame', icon: Flame, label: 'Spices' },
  { name: 'shirt', icon: Shirt, label: 'Textiles' },
  { name: 'palette', icon: Palette, label: 'Crafts' },
  { name: 'gem', icon: Gem, label: 'Jewelry' },
  { name: 'lamp', icon: Lamp, label: 'Decor' },
  { name: 'sparkles', icon: Sparkles, label: 'Puja' },
  { name: 'leaf', icon: Leaf, label: 'Wellness' },
  { name: 'image', icon: ImageIcon, label: 'Art' },
  { name: 'coffee', icon: Coffee, label: 'Beverages' },
  { name: 'nut', icon: Nut, label: 'Dry Fruits' },
  { name: 'gift', icon: Gift, label: 'Gifts' },
  { name: 'package', icon: Package, label: 'General' },
  { name: 'heart', icon: Heart, label: 'Favorites' },
  { name: 'star', icon: Star, label: 'Special' },
  { name: 'shopping-bag', icon: ShoppingBag, label: 'Shopping' },
  { name: 'home', icon: Home, label: 'Home' },
  { name: 'music', icon: Music, label: 'Music' },
  { name: 'book-open', icon: BookOpen, label: 'Books' },
];

export default function CategoryFormDialog({ open, onClose, category, onSave }) {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', image: '', banner: '', icon: 'package', display_order: 0 });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (category) {
      setFormData({ 
        name: category.name || '', 
        description: category.description || '', 
        image: category.image || '',
        banner: category.banner || '',
        icon: category.icon || 'package',
        display_order: category.display_order || 0
      });
    } else {
      setFormData({ name: '', description: '', image: '', banner: '', icon: 'package', display_order: 0 });
    }
  }, [category, open]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axiosInstance.post(`${API_URL}/api/upload-image`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData(prev => ({ ...prev, image: response.data.url }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, image: '' }));
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    try {
      setUploadingBanner(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axiosInstance.post(`${API_URL}/api/upload-image`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData(prev => ({ ...prev, banner: response.data.url }));
      toast.success('Banner uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleRemoveBanner = () => {
    setFormData(prev => ({ ...prev, banner: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Category name is required');
      return;
    }
    setLoading(true);
    try {
      await onSave(formData);
      onClose();
      toast.success(category ? 'Category updated' : 'Category created');
    } catch (error) {
      toast.error('Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const selectedIcon = ICON_OPTIONS.find(opt => opt.name === formData.icon);
  const SelectedIconComponent = selectedIcon?.icon || Package;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{category ? 'Edit Category' : 'Add New Category'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Category Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Category Name *</Label>
            <Input 
              id="name" 
              value={formData.name} 
              onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))} 
              placeholder="e.g., Traditional Sweets" 
              required 
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea 
              id="description" 
              value={formData.description} 
              onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))} 
              placeholder="Category description..." 
              rows={2} 
            />
          </div>

          {/* Icon Selection */}
          <div className="space-y-2">
            <Label>Category Icon</Label>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-emerald-600 rounded-full flex items-center justify-center">
                <SelectedIconComponent className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">{selectedIcon?.label || 'Select Icon'}</p>
                <p className="text-xs text-gray-500">Click below to change</p>
              </div>
            </div>
            <div className="grid grid-cols-5 gap-2 p-2 border rounded-lg max-h-40 overflow-y-auto">
              {ICON_OPTIONS.map((opt) => {
                const IconComp = opt.icon;
                return (
                  <button
                    key={opt.name}
                    type="button"
                    onClick={() => setFormData(prev => ({...prev, icon: opt.name}))}
                    className={`p-2 rounded-lg flex flex-col items-center gap-1 transition-all ${
                      formData.icon === opt.name 
                        ? 'bg-green-100 border-2 border-green-600' 
                        : 'hover:bg-gray-100 border border-transparent'
                    }`}
                    title={opt.label}
                  >
                    <IconComp className={`w-5 h-5 ${formData.icon === opt.name ? 'text-green-600' : 'text-gray-600'}`} />
                    <span className="text-[10px] text-gray-500 truncate w-full text-center">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Display Order */}
          <div className="space-y-2">
            <Label htmlFor="display_order">Display Order</Label>
            <Input 
              id="display_order" 
              type="number"
              min="0"
              value={formData.display_order} 
              onChange={(e) => setFormData(prev => ({...prev, display_order: parseInt(e.target.value) || 0}))} 
              placeholder="0" 
            />
            <p className="text-xs text-gray-500">Lower numbers appear first</p>
          </div>

          {/* Image Upload */}
          <div className="space-y-2">
            <Label>Category Tile Image (Optional)</Label>
            <p className="text-xs text-gray-500 mb-2">Square image shown in category grid on homepage</p>
            
            {!formData.image ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-green-600 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="category-image-upload"
                  disabled={uploading}
                />
                <label 
                  htmlFor="category-image-upload" 
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-8 h-8 text-gray-400 mb-1" />
                  <span className="text-sm text-gray-600">
                    {uploading ? 'Uploading...' : 'Upload tile image (optional)'}
                  </span>
                </label>
              </div>
            ) : (
              <div className="relative">
                <img 
                  src={formData.image} 
                  alt="Category preview" 
                  className="w-full h-32 object-cover rounded-lg border" 
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Banner Upload */}
          <div className="space-y-2">
            <Label>Category Banner (Optional)</Label>
            <p className="text-xs text-gray-500 mb-2">Wide banner image shown on category page (recommended: 1200x400px)</p>
            
            {!formData.banner ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-green-600 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBannerUpload}
                  className="hidden"
                  id="category-banner-upload"
                  disabled={uploadingBanner}
                />
                <label 
                  htmlFor="category-banner-upload" 
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-8 h-8 text-gray-400 mb-1" />
                  <span className="text-sm text-gray-600">
                    {uploadingBanner ? 'Uploading...' : 'Upload banner image (optional)'}
                  </span>
                </label>
              </div>
            ) : (
              <div className="relative">
                <img 
                  src={formData.banner} 
                  alt="Banner preview" 
                  className="w-full h-24 object-cover rounded-lg border" 
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleRemoveBanner}
                  className="absolute top-2 right-2"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || uploading} className="btn-primary">
              {loading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                category ? 'Update Category' : 'Add Category'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
